import { useState, type FormEvent } from 'react';
import { submitOutreachLead, type OutreachLead } from '../lib/api';

type InstitutionType = NonNullable<OutreachLead['institutionType']>;

const INSTITUTION_OPTIONS: { value: InstitutionType; label: string }[] = [
  { value: 'university', label: 'University' },
  { value: 'employer', label: 'Employer' },
  { value: 'health_plan', label: 'Health plan' },
  { value: 'other', label: 'Other' },
];

interface UnsupportedCommunityProps {
  email: string;
  /** Optional dismiss action — back to landing, etc. */
  onDismiss?: () => void;
  /** Optional flag to render without a <main> wrapper (used when this is
   *  inlined inside another page's layout). */
  embedded?: boolean;
}

/**
 * Shown when a visitor's email domain doesn't map to any partnered
 * community. Warm copy + a small lead form + a copy-paste template they
 * can forward to their wellness or counseling team. Two entry points:
 *
 *  - Landing pre-check (before the magic link is even sent)
 *  - App.tsx post-sign-in fallback (signed-in but no eligible community)
 */
export function UnsupportedCommunity({ email, onDismiss, embedded }: UnsupportedCommunityProps) {
  const domain = (email.split('@')[1] ?? email).toLowerCase();

  const [institutionName, setInstitutionName] = useState('');
  const [institutionType, setInstitutionType] = useState<InstitutionType>('university');
  const [institutionContact, setInstitutionContact] = useState('');
  const [message, setMessage] = useState('');
  const [notifyOnAvailable, setNotifyOnAvailable] = useState(true);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const personLabel =
    institutionType === 'employer'
      ? 'employees'
      : institutionType === 'health_plan'
        ? 'members'
        : 'students';

  const template = `Hi — I'd like to use LessAlone, a moderated peer-support service for ${personLabel} dealing with loneliness. They onboard one community at a time and I think it'd be a real fit here.

Their team can be reached at hello@lessalone.app, or learn more at https://lessalone.app.

Would you be open to a short conversation with them?

— ${email}`;

  async function copyTemplate() {
    try {
      await navigator.clipboard.writeText(template);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API blocked — surface a hint instead of failing silently.
      setError('Couldn’t copy automatically — select the text and copy it manually.');
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await submitOutreachLead({
      email,
      institutionName: institutionName.trim() || undefined,
      institutionType,
      institutionContact: institutionContact.trim() || undefined,
      message: message.trim() || undefined,
      notifyOnAvailable,
    });
    setBusy(false);
    if (result.ok) setSubmitted(true);
    else setError(result.error);
  }

  const body = submitted ? (
    <>
      <h1>Thank you — that helps a lot.</h1>
      <p>
        We've logged your interest in <strong>{institutionName.trim() || domain}</strong>. If a
        partnership comes together{' '}
        {notifyOnAvailable ? (
          <>
            we'll email you at <strong>{email}</strong>
          </>
        ) : (
          <>we'll get the word out</>
        )}{' '}
        as soon as it does.
      </p>
      <p className="muted">
        In the meantime, if you can forward the note below to whoever runs wellness, counseling, or
        benefits at your institution, that's the fastest way to make this happen.
      </p>

      <h2>Forward this to your wellness team</h2>
      <pre className="outreach-template">{template}</pre>
      <div className="row">
        <button type="button" className="secondary" onClick={() => void copyTemplate()}>
          {copied ? 'Copied ✓' : 'Copy this message'}
        </button>
        {onDismiss && (
          <button type="button" className="secondary" onClick={onDismiss}>
            Back
          </button>
        )}
      </div>
    </>
  ) : (
    <>
      <p className="step-label">No partnership yet</p>
      <h1>
        We're not at <strong>{domain}</strong> — but you can help change that.
      </h1>
      <p>
        LessAlone runs through partnered universities, employers, and health plans — that's how the
        community of verified peers stays small, moderated, and real. We don't have a partnership
        with <strong>{domain}</strong> yet, but if a few people there ask their wellness or
        counseling team about us, that's how partnerships start.
      </p>

      <form onSubmit={submit} className="stack">
        <label>
          Your institution
          <input
            type="text"
            value={institutionName}
            placeholder={
              institutionType === 'employer'
                ? 'e.g. Acme Inc'
                : institutionType === 'health_plan'
                  ? 'e.g. Blue Cross of [state]'
                  : 'e.g. State University'
            }
            maxLength={120}
            onChange={(event) => setInstitutionName(event.target.value)}
          />
        </label>

        <fieldset className="stack" style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend className="muted" style={{ marginBottom: '0.4rem' }}>
            What kind of institution?
          </legend>
          <div className="stack-row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
            {INSTITUTION_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`tag-chip${institutionType === opt.value ? ' on' : ''}`}
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="institutionType"
                  value={opt.value}
                  checked={institutionType === opt.value}
                  onChange={() => setInstitutionType(opt.value)}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </fieldset>

        <label>
          A wellness or counseling contact (optional)
          <input
            type="email"
            value={institutionContact}
            placeholder="wellness@yourinstitution.edu"
            maxLength={254}
            onChange={(event) => setInstitutionContact(event.target.value)}
          />
          <span className="muted" style={{ display: 'block', marginTop: '0.25rem' }}>
            If you give us a direct contact, we can reach out instead of waiting on the back
            channel.
          </span>
        </label>

        <label>
          Anything else? (optional)
          <textarea
            value={message}
            placeholder="A note for our partnerships team — context, urgency, or just hi."
            maxLength={1000}
            rows={3}
            onChange={(event) => setMessage(event.target.value)}
          />
        </label>

        <label className="consent-item" style={{ alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={notifyOnAvailable}
            onChange={(event) => setNotifyOnAvailable(event.target.checked)}
          />
          <span>
            Email me at <strong>{email}</strong> when LessAlone arrives at {domain}.
          </span>
        </label>

        <button type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Send this to the LessAlone team'}
        </button>
        {error && <p className="error">{error}</p>}
      </form>

      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px dashed var(--line)' }} />

      <h2>Or forward this to your wellness team</h2>
      <p className="muted">
        Copy-paste it. The faster they hear from someone at <strong>{domain}</strong>, the faster we
        can show up.
      </p>
      <pre className="outreach-template">{template}</pre>
      <div className="row">
        <button type="button" className="secondary" onClick={() => void copyTemplate()}>
          {copied ? 'Copied ✓' : 'Copy this message'}
        </button>
        {onDismiss && (
          <button type="button" className="secondary" onClick={onDismiss}>
            Back
          </button>
        )}
      </div>
    </>
  );

  if (embedded) {
    return <div className="card outreach-card">{body}</div>;
  }
  return (
    <main className="wide">
      <div className="card outreach-card">{body}</div>
    </main>
  );
}
